import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import {
  type AlertModalProps,
  AlertModal,
} from '@doxyme/ui/components/patterns';
import {
  getAppBasename,
  useAppRouter,
  useRouteChangeStart,
} from '../../hooks/useAppRouter';

export type NavigationConfirmationAlertProps = {
  alertTextProps: {
    title: string;
    description: string;
    primaryButtonText: string;
    secondaryButtonText: string;
  };
  shouldBlockNavigation: boolean;
  onLeave: () => void;
  domain: string;
  statusIcon?: AlertModalProps['statusIcon'];
  allowClientSideNavigation?: boolean;
};

export const NavigationConfirmationAlert = ({
  alertTextProps,
  shouldBlockNavigation,
  onLeave,
  domain,
  statusIcon = 'warning',
  allowClientSideNavigation = false,
}: NavigationConfirmationAlertProps): JSX.Element | null => {
  const router = useAppRouter();

  const [isOpen, setIsOpen] = useState(false);
  const [nextRoute, setNextRoute] = useState<string>('');
  const [routeChangeListening, setRouteChangeListening] = useState(
    !allowClientSideNavigation,
  );

  const shouldBlockRef = useRef(shouldBlockNavigation);
  shouldBlockRef.current = shouldBlockNavigation;

  const handleRouteChangeStart = useCallback(
    (url: string): void => {
      if (shouldBlockRef.current) {
        setIsOpen(true);
        setNextRoute(url);
        history.forward();
        throw `${domain} - blocked route change due to unsaved changes`;
      }
    },
    [domain],
  );

  const handleWindowClose = (event: BeforeUnloadEvent): void => {
    if (shouldBlockRef.current) {
      event.preventDefault();
    }
  };

  useRouteChangeStart(handleRouteChangeStart, routeChangeListening);

  useEffect(() => {
    window.addEventListener('beforeunload', handleWindowClose);

    return () => {
      window.removeEventListener('beforeunload', handleWindowClose);
    };
  }, []);

  useEffect(() => {
    if (!shouldBlockNavigation) {
      setRouteChangeListening(false);
    } else if (!allowClientSideNavigation) {
      setRouteChangeListening(true);
    }
  }, [shouldBlockNavigation, allowClientSideNavigation]);

  const stayOnPage = (): void => {
    setIsOpen(false);
    setNextRoute('');
  };

  const leavePage = (): void => {
    setIsOpen(false);

    onLeave();

    if (Boolean(nextRoute)) {
      setRouteChangeListening(false);
      router.push(nextRoute.replace(getAppBasename(), ''));
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <AlertModal
      isOpen={isOpen}
      statusIcon={statusIcon}
      title={alertTextProps.title}
      description={alertTextProps.description}
      onClose={stayOnPage}
      primaryButton={{
        text: alertTextProps.primaryButtonText,
        onClick: stayOnPage,
      }}
      secondaryButton={{
        text: alertTextProps.secondaryButtonText,
        onClick: leavePage,
      }}
    />
  );
};
